import { Input } from '../input'
import { GripVertical, Minus, Plus } from 'lucide-react'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'
import { MiniButton } from './mini-button'
import uniqueId from 'lodash.uniqueid'
import { mergeClasses } from '../../../helpers/styling'
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../table'
import { produce } from 'immer'

interface FixedLengthArray<
  T extends any,
  L extends number,
> extends Array<T> {
  0: T
  length: L
}

// Need id for stable sorting/react behaviour
type RowWithId<L extends number> = {
  id: string
  data: FixedLengthArray<string, L>
}

export function TextTableInput<
  ColumnName extends string,
  ColumnCount extends number,
>(props: {
  id?: string
  name?: string
  valueOnlyAsInitial?: boolean
  value: FixedLengthArray<string, ColumnCount>[] | undefined
  onChange: (value: FixedLengthArray<string, ColumnCount>[]) => void
  className?: string
  columnLabels: FixedLengthArray<ColumnName, ColumnCount>
  staticRows?: boolean
  disabledColumnIndexes?: number[]
}) {
  const [ rows, setRows ] = React.useState<RowWithId<ColumnCount>[]>(
    () => props.value?.map(toState) ?? []
  )

  React.useEffect(() => {
    if (props.valueOnlyAsInitial) return

    const isSameValues
      = props.value?.length === rows.length // we assume ColumnSize does not change
      && props.value.every((row, iR) => row.every((value, iC) =>
          value === rows[iR].data[iC]))
    if (isSameValues) return

    setRows(rowsWithId(props.value))
  }, [ props.value ])

  const setAndPublishItems = (newRows: RowWithId<ColumnCount>[]) => {
    setRows(newRows)
    props.onChange(newRows.map(row => row.data)) // TODO: type change
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const containerRef = React.useRef<HTMLTableElement>(null)

  const focusInput = (idxRow: number, idxCol: number) => {
    requestAnimationFrame(() => {
      containerRef.current
        ?.querySelector<HTMLInputElement>(`input[data-index="${idxRow},${idxCol}"]`)
        ?.focus()
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={e => {
        if (!e.over) return
        const oldIndex = rows.findIndex(i => i.id === e.active.id)
        const newIndex = rows.findIndex(i => i.id === e.over!.id)
        if (oldIndex !== newIndex) {
          setAndPublishItems(arrayMove(rows, oldIndex, newIndex))
          focusInput(newIndex, 0)
        }
      }}
    >
      <SortableContext
        items={rows.map(i => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <Table id={props.id} role="group" ref={containerRef}
          className={mergeClasses("flex flex-col gap-1 border rounded-md", props.className)}
        >

          <TableHeader className="px-3 pt-2">
            <TableRowNoData staticRows={props.staticRows}>
              {props.columnLabels.map((label, i) => (
                <TableHead className="flex-grow h-7 px-3" scope="col" key={i}>{label}</TableHead>
              ))}
            </TableRowNoData>
          </TableHeader>

          <TableBody className="border-t px-3 py-2">
            {!props.staticRows && (
              <TableRowNoData staticRows={props.staticRows}>
                <MiniButton title="Add a new row on top" className="w-full justify-center"
                  onClick={() => {
                    const next: RowWithId<ColumnCount>[] = [ newRow(props.columnLabels.length), ...rows ]
                    setAndPublishItems(next)
                    focusInput(next.length - 1, 0)
                  }}
                ><Plus size={11} /></MiniButton>
              </TableRowNoData>
            )}

            {rows.map((row, idxRow) => (
              <SortableRow key={row.id} id={row.id} rowData={row.data} idxRow={idxRow}
                staticRows={props.staticRows}
                disabledColumnIndexes={props.disabledColumnIndexes}
                last={rows.length - 1 === idxRow}
                onChange={(idxRow, idxCol, value) => {
                  const next = produce(rows, draft => {
                    draft[idxRow].data[idxCol] = value
                  })
                  setAndPublishItems(next)
                }}
                onAddBelow={idxRow => {
                  const next = [...rows]
                  next.splice(idxRow + 1, 0, newRow(props.columnLabels.length))
                  setAndPublishItems(next)
                  focusInput(idxRow + 1, 0)
                }}
                onRemove={idxRow => {
                  const next = rows.filter((_, idx) => idx !== idxRow)
                  setAndPublishItems(next)
                  focusInput(Math.max(0, idxRow - 1), 0)
                }}
                focusInput={focusInput}
              />
            ))}

            {!props.staticRows && (
              <TableRowNoData staticRows={props.staticRows}>
                <MiniButton title="Add a new row below" className="w-full justify-center"
                  onClick={() => {
                    const next: RowWithId<ColumnCount>[] = [ ...rows, newRow(props.columnLabels.length) ]
                    setAndPublishItems(next)
                    focusInput(next.length - 1, 0)
                  }}
                ><Plus size={11} /></MiniButton>
              </TableRowNoData>
            )}
          </TableBody>
        </Table>
      </SortableContext>
    </DndContext>
  )
}

function SortableRow<ColumnCount extends number>(props: {
  last: boolean
  rowData: FixedLengthArray<string, ColumnCount>
  id: string
  idxRow: number
  onChange: (idxRow: number, idxCol: number, v: string) => void
  onAddBelow: (idxRow: number) => void
  onRemove: (idxRow: number) => void
  focusInput: (idxRow: number, idxCol: number) => void
  staticRows: boolean | undefined
  disabledColumnIndexes: number[] | undefined
}): React.ReactNode {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <TableRow ref={setNodeRef} style={style} className="flex items-center gap-1 hover:bg-inherit border-0">

      {!props.staticRows && (
        <button type="button" {...attributes} {...listeners}
          className="inline-flex flex-shrink items-center opacity-50 cursor-move hover:opacity-80"
        ><GripVertical size={11} /></button>
      )}

      {props.rowData.map((value, idxCol) => (
        <TableCell className="flex-grow">
          <Input
            disabled={props.disabledColumnIndexes?.includes(idxCol)}
            data-index={props.idxRow + ',' + idxCol}
            className="
              text-xs !px-[12px] h-7 rounded-sm
              focus:ring-2 focus:ring-blue-500
              focus-visible:ring-2 focus-visible:ring-blue-500
            "
            value={value}
            onChange={e => props.onChange(props.idxRow, idxCol, e.target.value)}
            onKeyDown={e => {
              const { selectionStart, selectionEnd, value } = e.currentTarget
              const atStart = selectionStart === 0 && selectionEnd === 0 && idxCol === 0
              const atEnd =
                selectionStart === value.length &&
                selectionEnd === value.length

              if (e.key === 'Enter') {
                e.preventDefault()
                props.onAddBelow(props.idxRow)
              }

              if (e.key === 'Backspace' && atStart) {
                e.preventDefault()
                props.onRemove(props.idxRow)
              }

              if (e.key === 'Delete' && atEnd) {
                e.preventDefault()
                props.onRemove(props.idxRow + 1)
              }

              if (e.key === 'ArrowUp') {
                e.preventDefault()
                props.focusInput(props.idxRow - 1, idxCol)
              }

              if (e.key === 'ArrowDown') {
                e.preventDefault()
                props.focusInput(props.idxRow + 1, idxCol)
              }

              if (e.key === 'ArrowLeft') {
                e.preventDefault()
                props.focusInput(props.idxRow, idxCol - 1)
              }

              if (e.key === 'ArrowRight') {
                e.preventDefault()
                props.focusInput(props.idxRow, idxCol + 1)
              }
            }}
          />
        </TableCell>
      ))}

      {!props.staticRows && (<>
        <MiniButton title="Remove" tabIndex={-1}
          onClick={() => props.onRemove(props.idxRow)}
        ><Minus size={11} /></MiniButton>
        <MiniButton title="Add below" tabIndex={-1}
          onClick={() => props.onAddBelow(props.idxRow)}
        ><Plus size={11} /></MiniButton>
      </>)}
    </TableRow>
  )
}

function TableRowNoData(props: {
  children: React.ReactNode,
  staticRows: boolean | undefined
}): React.ReactNode {
  return (
    <TableRow className="hover:bg-inherit flex items-center gap-1 border-0">
      {!props.staticRows && (<GripVertical size={11} className="invisible" />)} {/* represents space of grip element */}
      {props.children}
      {!props.staticRows && (<GripVertical size={11} className="invisible m-1" />)} {/* represents space of - button */}
      {!props.staticRows && (<GripVertical size={11} className="invisible m-1" />)} {/* represents space of + button */}
    </TableRow>
  )
}

function rowsWithId<ColumnCount extends number>(
  rows?: FixedLengthArray<string, ColumnCount>[],
): RowWithId<ColumnCount>[] {
  return rows?.map(toState) ?? []
}

function newRow<ColumnCount extends number>(
  columnCount: ColumnCount,
): RowWithId<ColumnCount> {
  const data = Array.from({length: columnCount}).map(() => '') as FixedLengthArray<string, ColumnCount>
  return toState(data)
}

function toState<ColumnCount extends number>(
  row: FixedLengthArray<string, ColumnCount>,
): RowWithId<ColumnCount> {
  return {id: uniqueId(), data: row}
}
